import { PALETTE } from '../StageData';
import { ShadingPipeline } from '../../engine/ShadingPipeline';

export type ShapeDrawFn = (
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  mode: 'base' | 'outline' | 'targetPreview'
) => void;

export const furnitureDrawFns: Record<number, ShapeDrawFn> = {
  // STAGE 1: 木製スツール
  1: (ctx, cx, cy, mode) => {
    const drawStool = () => {
      ctx.beginPath();
      ctx.ellipse(cx, cy - 40, 110, 36, 0, 0, Math.PI * 2);
    };
    const drawLegs = () => {
      ctx.beginPath();
      ctx.moveTo(cx - 70, cy - 30); ctx.lineTo(cx - 95, cy + 130); ctx.lineTo(cx - 70, cy + 130); ctx.lineTo(cx - 50, cy - 30);
      ctx.moveTo(cx + 70, cy - 30); ctx.lineTo(cx + 95, cy + 130); ctx.lineTo(cx + 70, cy + 130); ctx.lineTo(cx + 50, cy - 30);
      ctx.moveTo(cx - 10, cy - 20); ctx.lineTo(cx - 10, cy + 120); ctx.lineTo(cx + 10, cy + 120); ctx.lineTo(cx + 10, cy - 20);
      ctx.moveTo(cx - 78, cy + 50); ctx.lineTo(cx + 78, cy + 50); ctx.lineTo(cx + 78, cy + 64); ctx.lineTo(cx - 78, cy + 64);
      ctx.closePath();
    };

    if (mode === 'base') {
      ctx.fillStyle = '#FAF8F5';
      drawLegs(); ctx.fill();
      drawStool(); ctx.fill();
    } else if (mode === 'outline') {
      ShadingPipeline.renderCarvedOutline(ctx, () => {
        drawLegs(); ctx.stroke();
        drawStool(); ctx.stroke();
      });
    } else if (mode === 'targetPreview') {
      ctx.fillStyle = ShadingPipeline.createArtisanGradient(ctx, cx, cy, 200, 200, PALETTE.wood, 'wood');
      drawLegs(); ctx.fill();
      ctx.fillStyle = ShadingPipeline.createArtisanGradient(ctx, cx, cy - 40, 220, 72, PALETTE.blue, 'lacquer');
      drawStool(); ctx.fill();
      ShadingPipeline.renderSpecularGlint(ctx, cx - 35, cy - 48, 45, 12, 0.45);
    }
  },

  // STAGE 2: レトロ丸型ポスト
  2: (ctx, cx, cy, mode) => {
    const drawPostbox = () => {
      ctx.beginPath();
      ctx.arc(cx, cy - 90, 85, Math.PI, 0);
      ctx.lineTo(cx + 85, cy + 110);
      ctx.lineTo(cx - 85, cy + 110);
      ctx.closePath();
    };
    const drawBaseAndMouth = () => {
      ctx.beginPath();
      ctx.ellipse(cx, cy + 125, 95, 25, 0, 0, Math.PI * 2);
      ctx.roundRect(cx - 55, cy - 35, 110, 24, 6);
    };

    if (mode === 'base') {
      ctx.fillStyle = '#FAF8F5';
      drawPostbox(); ctx.fill();
      drawBaseAndMouth(); ctx.fill();
    } else if (mode === 'outline') {
      ShadingPipeline.renderCarvedOutline(ctx, () => {
        drawPostbox(); ctx.stroke();
        drawBaseAndMouth(); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(cx - 65, cy + 25); ctx.lineTo(cx + 65, cy + 25); ctx.stroke();
        ctx.strokeRect(cx - 40, cy + 35, 80, 55);
      });
    } else if (mode === 'targetPreview') {
      ctx.fillStyle = ShadingPipeline.createArtisanGradient(ctx, cx, cy, 180, 250, PALETTE.crimson, 'lacquer');
      drawPostbox(); ctx.fill();
      ctx.fillStyle = PALETTE.dark;
      ctx.beginPath(); ctx.ellipse(cx, cy + 125, 95, 25, 0, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = PALETTE.black;
      ctx.beginPath(); ctx.roundRect(cx - 55, cy - 35, 110, 24, 6); ctx.fill();
      ctx.fillStyle = PALETTE.white;
      ctx.fillRect(cx - 40, cy + 35, 80, 55);
      ShadingPipeline.renderSpecularGlint(ctx, cx - 35, cy - 90, 40, 18, 0.4);
    }
  },

  // STAGE 3: 青磁と茜の花瓶
  3: (ctx, cx, cy, mode) => {
    const drawVase = () => {
      ctx.beginPath();
      ctx.moveTo(cx - 35, cy - 140);
      ctx.bezierCurveTo(cx - 45, cy - 100, cx - 15, cy - 70, cx - 80, cy + 20);
      ctx.bezierCurveTo(cx - 95, cy + 80, cx - 45, cy + 140, cx - 40, cy + 140);
      ctx.lineTo(cx + 40, cy + 140);
      ctx.bezierCurveTo(cx + 45, cy + 140, cx + 95, cy + 80, cx + 80, cy + 20);
      ctx.bezierCurveTo(cx + 15, cy - 70, cx + 45, cy - 100, cx + 35, cy - 140);
      ctx.closePath();
    };

    if (mode === 'base') {
      ctx.fillStyle = '#FAF8F5'; drawVase(); ctx.fill();
    } else if (mode === 'outline') {
      ShadingPipeline.renderCarvedOutline(ctx, () => {
        drawVase(); ctx.stroke();
        ctx.beginPath(); ctx.ellipse(cx, cy - 140, 35, 10, 0, 0, Math.PI * 2); ctx.stroke();
      });
    } else if (mode === 'targetPreview') {
      const grad = ctx.createLinearGradient(cx, cy - 140, cx, cy + 140);
      grad.addColorStop(0, PALETTE.cyan);
      grad.addColorStop(0.55, PALETTE.coral);
      grad.addColorStop(1, PALETTE.gold);
      ctx.fillStyle = grad; drawVase(); ctx.fill();
      ShadingPipeline.renderSpecularGlint(ctx, cx - 30, cy + 10, 18, 55, 0.55);
    }
  },

  // STAGE 4: ダイニングチェア
  4: (ctx, cx, cy, mode) => {
    const drawChair = () => {
      ctx.beginPath();
      ctx.roundRect(cx - 65, cy - 140, 130, 95, 14);
      ctx.roundRect(cx - 85, cy - 25, 170, 36, 10);
      ctx.moveTo(cx - 75, cy + 10); ctx.lineTo(cx - 85, cy + 140);
      ctx.moveTo(cx + 75, cy + 10); ctx.lineTo(cx + 85, cy + 140);
      ctx.moveTo(cx - 35, cy + 10); ctx.lineTo(cx - 40, cy + 120);
      ctx.moveTo(cx + 35, cy + 10); ctx.lineTo(cx + 40, cy + 120);
    };

    if (mode === 'base') {
      ctx.fillStyle = '#FAF8F5'; drawChair(); ctx.fill();
    } else if (mode === 'outline') {
      ShadingPipeline.renderCarvedOutline(ctx, () => {
        drawChair(); ctx.stroke();
      });
    } else if (mode === 'targetPreview') {
      ctx.fillStyle = ShadingPipeline.createArtisanGradient(ctx, cx, cy, 170, 200, PALETTE.wood, 'wood');
      drawChair(); ctx.fill();
      ctx.fillStyle = ShadingPipeline.createArtisanGradient(ctx, cx, cy - 25, 170, 36, PALETTE.emerald, 'cloth');
      ctx.beginPath(); ctx.roundRect(cx - 85, cy - 25, 170, 36, 10); ctx.fill();
    }
  },

  // STAGE 5: デスクランプ
  5: (ctx, cx, cy, mode) => {
    const drawLamp = () => {
      ctx.beginPath();
      ctx.ellipse(cx, cy - 90, 85, 45, 0, Math.PI, 0);
      ctx.moveTo(cx, cy - 90); ctx.lineTo(cx + 40, cy); ctx.lineTo(cx, cy + 90);
      ctx.ellipse(cx, cy + 105, 65, 20, 0, 0, Math.PI * 2);
    };

    if (mode === 'base') {
      ctx.fillStyle = '#FAF8F5'; drawLamp(); ctx.fill();
    } else if (mode === 'outline') {
      ShadingPipeline.renderCarvedOutline(ctx, () => {
        drawLamp(); ctx.stroke();
        ctx.beginPath(); ctx.ellipse(cx, cy - 90, 85, 20, 0, 0, Math.PI * 2); ctx.stroke();
      });
    } else if (mode === 'targetPreview') {
      ctx.fillStyle = ShadingPipeline.createArtisanGradient(ctx, cx, cy - 90, 170, 55, PALETTE.emerald, 'glass');
      ctx.beginPath(); ctx.ellipse(cx, cy - 90, 85, 45, 0, Math.PI, 0); ctx.fill();
      ctx.fillStyle = ShadingPipeline.createArtisanGradient(ctx, cx, cy + 105, 130, 30, PALETTE.brass, 'metallic');
      ctx.beginPath(); ctx.ellipse(cx, cy + 105, 65, 20, 0, 0, Math.PI * 2); ctx.fill();
      ShadingPipeline.renderSpecularGlint(ctx, cx - 25, cy - 105, 35, 12, 0.6);
    }
  },

  // STAGE 6: レトロ八角掛け時計
  6: (ctx, cx, cy, mode) => {
    const drawOctagon = (r: number) => {
      ctx.beginPath();
      for (let i = 0; i < 8; i++) {
        const a = (i * Math.PI) / 4 + Math.PI / 8;
        const px = cx + Math.cos(a) * r;
        const py = cy - 30 + Math.sin(a) * r;
        if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
      }
      ctx.closePath();
    };

    if (mode === 'base') {
      ctx.fillStyle = '#FAF8F5'; drawOctagon(105); ctx.fill();
      ctx.beginPath(); ctx.arc(cx, cy + 110, 26, 0, Math.PI * 2); ctx.fill();
    } else if (mode === 'outline') {
      ShadingPipeline.renderCarvedOutline(ctx, () => {
        drawOctagon(105); ctx.stroke();
        drawOctagon(82); ctx.stroke();
        ctx.beginPath(); ctx.arc(cx, cy - 30, 68, 0, Math.PI * 2); ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(cx, cy + 45); ctx.lineTo(cx, cy + 105);
        ctx.arc(cx, cy + 110, 26, 0, Math.PI * 2);
        ctx.stroke();
      });
    } else if (mode === 'targetPreview') {
      ctx.fillStyle = ShadingPipeline.createArtisanGradient(ctx, cx, cy - 30, 210, 210, PALETTE.darkWood, 'wood');
      drawOctagon(105); ctx.fill();
      ctx.fillStyle = PALETTE.ivory;
      ctx.beginPath(); ctx.arc(cx, cy - 30, 68, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = ShadingPipeline.createArtisanGradient(ctx, cx, cy + 110, 52, 52, PALETTE.gold, 'metallic');
      ctx.beginPath(); ctx.arc(cx, cy + 110, 26, 0, Math.PI * 2); ctx.fill();
      ShadingPipeline.renderSpecularGlint(ctx, cx - 18, cy - 55, 30, 15, 0.4);
    }
  },

  // STAGE 7: 3段オープン本棚
  7: (ctx, cx, cy, mode) => {
    const drawBookshelf = () => {
      ctx.beginPath();
      ctx.roundRect(cx - 95, cy - 130, 190, 260, 8);
    };

    if (mode === 'base') {
      ctx.fillStyle = '#FAF8F5'; drawBookshelf(); ctx.fill();
    } else if (mode === 'outline') {
      ShadingPipeline.renderCarvedOutline(ctx, () => {
        drawBookshelf(); ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(cx - 95, cy - 45); ctx.lineTo(cx + 95, cy - 45);
        ctx.moveTo(cx - 95, cy + 40); ctx.lineTo(cx + 95, cy + 40);
        ctx.stroke();
        ctx.strokeRect(cx - 75, cy - 115, 30, 65);
        ctx.strokeRect(cx - 40, cy - 115, 35, 65);
      });
    } else if (mode === 'targetPreview') {
      ctx.fillStyle = ShadingPipeline.createArtisanGradient(ctx, cx, cy, 190, 260, PALETTE.darkWood, 'wood');
      drawBookshelf(); ctx.fill();
      ctx.fillStyle = PALETTE.cyan; ctx.fillRect(cx - 75, cy - 115, 30, 65);
      ctx.fillStyle = PALETTE.coral; ctx.fillRect(cx - 40, cy - 115, 35, 65);
      ctx.fillStyle = PALETTE.gold; ctx.fillRect(cx + 25, cy - 35, 45, 70);
    }
  },

  // STAGE 8: ロータス・カフェテーブル
  8: (ctx, cx, cy, mode) => {
    const drawTabletop = () => {
      ctx.beginPath();
      ctx.ellipse(cx, cy - 45, 125, 48, 0, 0, Math.PI * 2);
    };
    const drawLegs = () => {
      ctx.beginPath();
      ctx.moveTo(cx - 75, cy - 35); ctx.lineTo(cx - 95, cy + 125);
      ctx.moveTo(cx + 75, cy - 35); ctx.lineTo(cx + 95, cy + 125);
      ctx.moveTo(cx, cy - 25); ctx.lineTo(cx, cy + 135);
    };

    if (mode === 'base') {
      ctx.fillStyle = '#FAF8F5'; drawLegs(); ctx.fill(); drawTabletop(); ctx.fill();
    } else if (mode === 'outline') {
      ShadingPipeline.renderCarvedOutline(ctx, () => {
        drawLegs(); ctx.stroke();
        drawTabletop(); ctx.stroke();
        ctx.beginPath(); ctx.ellipse(cx, cy - 45, 110, 40, 0, 0, Math.PI * 2); ctx.stroke();
      });
    } else if (mode === 'targetPreview') {
      ctx.fillStyle = ShadingPipeline.createArtisanGradient(ctx, cx, cy + 50, 190, 170, PALETTE.wood, 'wood');
      drawLegs(); ctx.fill();
      ctx.fillStyle = ShadingPipeline.createArtisanGradient(ctx, cx, cy - 45, 250, 96, PALETTE.ivory, 'ceramic');
      drawTabletop(); ctx.fill();
      ctx.strokeStyle = PALETTE.gold; ctx.lineWidth = 4;
      ctx.beginPath(); ctx.ellipse(cx, cy - 45, 110, 40, 0, 0, Math.PI * 2); ctx.stroke();
      ShadingPipeline.renderSpecularGlint(ctx, cx - 40, cy - 55, 45, 15, 0.45);
    }
  },

  // STAGE 9: ヴィンテージ置き鏡
  9: (ctx, cx, cy, mode) => {
    const drawMirror = () => {
      ctx.beginPath();
      ctx.ellipse(cx, cy - 35, 75, 105, 0, 0, Math.PI * 2);
    };

    if (mode === 'base') {
      ctx.fillStyle = '#FAF8F5'; drawMirror(); ctx.fill();
      ctx.fillRect(cx - 8, cy + 70, 16, 60);
      ctx.beginPath(); ctx.ellipse(cx, cy + 130, 60, 18, 0, 0, Math.PI * 2); ctx.fill();
    } else if (mode === 'outline') {
      ShadingPipeline.renderCarvedOutline(ctx, () => {
        drawMirror(); ctx.stroke();
        ctx.beginPath(); ctx.ellipse(cx, cy - 35, 60, 88, 0, 0, Math.PI * 2); ctx.stroke();
        ctx.strokeRect(cx - 8, cy + 70, 16, 60);
        ctx.beginPath(); ctx.ellipse(cx, cy + 130, 60, 18, 0, 0, Math.PI * 2); ctx.stroke();
      });
    } else if (mode === 'targetPreview') {
      ctx.fillStyle = ShadingPipeline.createArtisanGradient(ctx, cx, cy - 35, 150, 210, PALETTE.gold, 'metallic');
      drawMirror(); ctx.fill();
      ctx.fillStyle = ShadingPipeline.createArtisanGradient(ctx, cx, cy - 35, 120, 176, PALETTE.cyan, 'glass');
      ctx.beginPath(); ctx.ellipse(cx, cy - 35, 60, 88, 0, 0, Math.PI * 2); ctx.fill();
      ShadingPipeline.renderSpecularGlint(ctx, cx - 20, cy - 65, 30, 45, 0.55);
    }
  },

  // STAGE 10: 蒔絵アンティーク箪笥
  10: (ctx, cx, cy, mode) => {
    const drawCabinet = () => {
      ctx.beginPath();
      ctx.roundRect(cx - 105, cy - 110, 210, 220, 10);
    };

    if (mode === 'base') {
      ctx.fillStyle = '#FAF8F5'; drawCabinet(); ctx.fill();
    } else if (mode === 'outline') {
      ShadingPipeline.renderCarvedOutline(ctx, () => {
        drawCabinet(); ctx.stroke();
        ctx.strokeRect(cx - 90, cy - 95, 180, 55);
        ctx.strokeRect(cx - 90, cy - 30, 180, 55);
        ctx.strokeRect(cx - 90, cy + 35, 180, 55);
        ctx.beginPath(); ctx.arc(cx, cy - 68, 12, 0, Math.PI * 2); ctx.stroke();
        ctx.beginPath(); ctx.arc(cx, cy - 3, 12, 0, Math.PI * 2); ctx.stroke();
        ctx.beginPath(); ctx.arc(cx, cy + 62, 12, 0, Math.PI * 2); ctx.stroke();
      });
    } else if (mode === 'targetPreview') {
      ctx.fillStyle = ShadingPipeline.createArtisanGradient(ctx, cx, cy, 210, 220, PALETTE.black, 'lacquer');
      drawCabinet(); ctx.fill();
      ctx.fillStyle = ShadingPipeline.createArtisanGradient(ctx, cx, cy - 68, 180, 55, PALETTE.crimson, 'lacquer');
      ctx.fillRect(cx - 90, cy - 95, 180, 55);
      ctx.fillRect(cx - 90, cy - 30, 180, 55);
      ctx.fillRect(cx - 90, cy + 35, 180, 55);
      ctx.fillStyle = PALETTE.gold;
      ctx.beginPath(); ctx.arc(cx, cy - 68, 12, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath(); ctx.arc(cx, cy - 3, 12, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath(); ctx.arc(cx, cy + 62, 12, 0, Math.PI * 2); ctx.fill();
      ShadingPipeline.renderSpecularGlint(ctx, cx - 40, cy - 70, 45, 14, 0.4);
    }
  }
};
